<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useNotification } from '@kyvg/vue3-notification'
import ModalBox from './ModalBox.vue'
import FormButton from '../Atoms/FormButton.vue'
import FormInput from '../Atoms/FormInput.vue'
import FormSwitch from '../Atoms/FormSwitch.vue'
import Alert from '../Atoms/Alert.vue'
import Tooltip from '../Atoms/Tooltip.vue'
import IconBase from '../icon/IconBase.vue'
import IconChevronLeft from '../icon/IconChevronLeft.vue'
import IconCheckmark from '../icon/IconCheckmark.vue'
import IconMediaFileWarning from '../icon/IconMediaFileWarning.vue'
import TeamPermissionRow from '../Team/TeamPermissionRow.vue'
import TeamContentScopePicker from '../Team/TeamContentScopePicker.vue'
import ProjectDot from '../project/ProjectDot.vue'
import Spinner from '../ui/Spinner.vue'
import { useLegacyModalBridge } from '@/composables/useLegacyModalBridge'
import UserService from '@/services/UserService'
import { track, EVENTS } from '@/lib/tracking'
import { accessSummarySentence } from '@/Utils/teamAccessSummary'
import {
	applyRowLevel,
	areAllProductRowsOff,
	canUseInviteOthersFeature,
	chatOnlyApplies,
	createAdminPermissions,
	createModeratorPreset,
	groupedPermissionRows,
	hasTeamStreamSharing,
	hasTeamVodSharing,
	inviteOthersTooltip,
	levelFromPermissions,
	normalizePermissions,
	scopeIsInvalid,
	scopeLockMessage,
	scopeModeFromIds,
	setAdvancedFlag,
	setChatModerationOnly,
	visiblePermissionRows,
} from '@/Utils/teamPermissionRows'

const MODAL_ID = 'team-member-modal'
const EMAIL_RE = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/

const LOOKUP_DEBOUNCE_MS = 350

const props = defineProps({
	teamId: { type: String, default: '' },
	members: { type: Array, default: () => [] },
	member: { type: Object, default: null },
	teamName: { type: String, default: '' },
	// The workspace the Team page is standing in: the one an invitation or a
	// grant is bound to. Passed explicitly on submit so the server never has
	// to guess from the session.
	activeProject: { type: Object, default: null },
	// "Add <member> to <workspace>" from a row: the person is known, so the sheet
	// opens with their name and email filled in and locked.
	prefill: { type: Object, default: null },
	// Distinct people already holding a seat vs the plan limit. New people are
	// blocked at the limit; someone already on the team is not, because adding
	// them to another workspace takes no seat.
	atSeatLimit: { type: Boolean, default: false },
	seatLimit: { type: Number, default: 0 },
})

/**
 * `invite-sent`: a new person was emailed an invitation into this workspace.
 * `member-added`: someone already on the team was granted this workspace (no
 * email to accept); the roster needs a reload, not just the pending list.
 */
const emit = defineEmits(['invite-sent', 'member-added', 'update-permissions'])

const store = useStore()
const { notify } = useNotification()

const name = ref('')
const email = ref('')
const emailError = ref('')
const emailExists = ref(false)
const emailLocked = ref(false)
const processing = ref(false)
const loading = ref(false)
const submitError = ref('')
const permissions = ref(createModeratorPreset())
const scopeModes = ref({ live: 'all', vod: 'all' })
const memberId = ref(null)

// --- "is this person already on the team?" ---------------------------------
// state: idle | checking | found | none. `member` is the server's answer:
// { id, name, email, workspaces: [{ id, name, color, role, isEnabled }] }.
const lookup = ref({ state: 'idle', member: null })
// The workspace whose permissions were copied in as a starting point, if any.
const sourceGrant = ref(null)
const loadingSource = ref(false)
let lookupTimer = null
let lookupSeq = 0

const isEdit = computed(() => !!props.member)
const role = computed(() => permissions.value?.role || 'moderator')
const isModerator = computed(() => role.value === 'moderator')

const projectName = computed(() => props.activeProject?.name || 'this workspace')
const existing = computed(() => (lookup.value.state === 'found' ? lookup.value.member : null))
const existingName = computed(() => existing.value?.name || name.value || 'This person')
const grantHere = computed(() => (
	existing.value?.workspaces?.find((w) => w.id === props.activeProject?.id) || null
))
const otherGrants = computed(() => (
	(existing.value?.workspaces || []).filter((w) => w.id !== props.activeProject?.id)
))
const alreadyHere = computed(() => !!grantHere.value && grantHere.value.isEnabled !== false)
const pausedHere = computed(() => !!grantHere.value && grantHere.value.isEnabled === false)
// Adding someone who is already on the team: a grant, not an invitation.
const isGrant = computed(() => !isEdit.value && !!existing.value && !alreadyHere.value)
// A brand-new person at the seat limit cannot be invited; the server would
// refuse, so say it here instead.
const seatBlocked = computed(() => !isEdit.value && props.atSeatLimit && !existing.value && lookup.value.state !== 'checking')

const describeGrant = (w) => {
	const roleLabel = w.role ? w.role.charAt(0).toUpperCase() + w.role.slice(1) : 'Moderator'
	return w.isEnabled === false ? `paused in ${w.name}` : `${roleLabel} in ${w.name}`
}
const otherGrantsSentence = computed(() => {
	const parts = otherGrants.value.map(describeGrant)
	if (!parts.length) return ''
	if (parts.length === 1) return parts[0]
	return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
})

const baseSubscription = computed(() => store.getters['User/baseSubscription'])
const addonSubscriptions = computed(() => store.getters['User/addonSubscriptions'])
const subscriptions = computed(() => store.getters['User/subscriptions'])
const liveStreams = computed(() => store.getters['Streams/allInOneStreams'] || [])
const vodFolders = computed(() => store.getters['Streams/vodStreams'] || [])

const productRows = computed(() => visiblePermissionRows(baseSubscription.value, addonSubscriptions.value))
const productSections = computed(() => groupedPermissionRows(productRows.value))
const canInviteOthers = computed(() => canUseInviteOthersFeature(addonSubscriptions.value))
const inviteTooltip = computed(() => inviteOthersTooltip(addonSubscriptions.value))
const canPickStreams = computed(() => hasTeamStreamSharing(subscriptions.value))
const canPickVods = computed(() => hasTeamVodSharing(subscriptions.value))

// Names the workspace only once the account has more than one: with a single
// workspace the qualifier is noise, with several it is the answer to "what will
// this person be able to see?".
const scopedToProject = computed(() => store.getters['Projects/hasMultipleProjects'] && !!props.activeProject)

// --- which workspaces this person is being added to ------------------------
// One invitation can cover several, so the decision is made here rather than by
// reopening the sheet from each workspace in turn. The permissions below apply
// to all of them and can be tuned per workspace afterwards from the member list.
//
// 'all' is not shorthand for ticking everything: it is a standing grant the
// server honours for workspaces created later too, so it stays true as the
// account grows. It starts on 'specific' all the same — somebody who fills in a
// name and sends gets the workspace they were looking at, not the account.
const projectScope = ref('specific')
const selectedProjectIds = ref([])

const availableProjects = computed(() => store.getters['Projects/projects'] || [])
// Only worth asking when there is a choice; with one workspace the answer is
// always the same one. An edit asks it too, but one at a time — see below.
const showProjectPicker = computed(() => !isEdit.value && availableProjects.value.length > 1)
const allProjects = computed(() => showProjectPicker.value && projectScope.value === 'all')

// --- which workspace an edit applies to ------------------------------------
// A member holds one grant per workspace, so "edit access" is only a question
// once the workspace is named. It used to go unnamed: the request carried no
// `workspaceId` and the server fell back to whichever workspace the session was
// standing in, so somebody who is a Moderator in one and an Admin in another
// opened as whichever the session happened to hold. Naming it also answers
// "put them in another workspace" without a second invitation, because a save
// against a workspace they hold no grant in is what creates one.
const editWorkspaceId = ref(null)
const editWorkspace = computed(() => (
	availableProjects.value.find((p) => p.id === editWorkspaceId.value) || props.activeProject || null
))
const showEditWorkspacePicker = computed(() => isEdit.value && availableProjects.value.length > 1)
const memberWorkspaceIds = computed(() => new Set((props.member?.workspaces || []).map((w) => w.id)))
// True while the picker names a workspace this member is not in yet, where
// there is no grant to read and saving grants access rather than amending it.
const grantIsNew = computed(() => (
	isEdit.value && !!editWorkspaceId.value && !memberWorkspaceIds.value.has(editWorkspaceId.value)
))

// Workspaces an existing member already holds, which the picker offers as
// settled rather than selectable: re-sending them would silently overwrite the
// permissions they have there, which is not what adding somebody means.
const heldProjectIds = computed(() => new Set(
	(existing.value?.workspaces || []).filter((w) => w.isEnabled !== false).map((w) => w.id)
))
const lockedProjectIds = computed(() => [...heldProjectIds.value])

// The picker works in `_id`, the way the stream scopes beside it do.
const projectItems = computed(() => availableProjects.value.map((p) => ({ _id: p.id, name: p.name })))

const selectedProjects = computed(() => (
	availableProjects.value.filter((p) => selectedProjectIds.value.includes(p.id))
))
const selectedProjectNames = computed(() => {
	const names = selectedProjects.value.map((p) => p.name)
	if (names.length < 2) return names[0] || projectName.value
	return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
})

const title = computed(() => {
	if (isEdit.value) return `Edit ${props.member?.name || 'member'}`
	if (isGrant.value) {
		if (allProjects.value) return `Add ${existingName.value} to every workspace`
		const count = selectedProjectIds.value.length
		return count > 1
			? `Add ${existingName.value} to ${count} workspaces`
			: `Add ${existingName.value} to ${showProjectPicker.value ? selectedProjectNames.value : projectName.value}`
	}
	return 'Invite a team member'
})
const subtitle = computed(() => {
	if (isEdit.value) {
		return scopedToProject.value
			? `Their role and access in ${projectName.value}. Other workspaces are not affected.`
			: 'Adjust their role and access.'
	}
	if (isGrant.value) {
		return pausedHere.value
			? `${existingName.value}'s access to ${projectName.value} is paused. Adding them again re-enables it with the permissions below.`
			: `${existingName.value} already has an account, so there is nothing to accept. We'll let them know they've been added.`
	}
	const base = `They'll get an email to join ${props.teamName || 'your team'}.`
	return showProjectPicker.value
		? `${base} Pick the workspaces they should have access to — one email covers all of them.`
		: `${base} You can change this anytime.`
})
const primaryLabel = computed(() => {
	if (isEdit.value) return 'Save changes'
	if (!isGrant.value) return 'Send invitation'
	if (allProjects.value) return 'Add to every workspace'
	const count = selectedProjectIds.value.length
	return showProjectPicker.value && count > 1 ? `Add to ${count} workspaces` : `Add to ${selectedProjectNames.value}`
})

const summarySentence = computed(() => accessSummarySentence(permissions.value, {
	rows: productRows.value,
	role: role.value,
}))

const nameInvalid = computed(() => !name.value || !String(name.value).trim())
const emailInvalid = computed(() => !!emailError.value || !email.value || !EMAIL_RE.test(email.value || ''))

const scopeInvalid = computed(() => {
	if (!isModerator.value) return false
	const liveOn = levelFromPermissions(permissions.value, productRows.value.find((r) => r.scope === 'live')) !== 'none'
	const vodOn = levelFromPermissions(permissions.value, productRows.value.find((r) => r.scope === 'vod')) !== 'none'
	if (liveOn && canPickStreams.value && scopeIsInvalid(scopeModes.value.live, permissions.value.liveStreamAccessIds)) return true
	if (vodOn && canPickVods.value && scopeIsInvalid(scopeModes.value.vod, permissions.value.vodStreamAccessIds)) return true
	return false
})

const canSubmit = computed(() => {
	if (processing.value || loading.value || loadingSource.value) return false
	if (!isEdit.value && (nameInvalid.value || emailInvalid.value)) return false
	// Wait for the lookup so the button never flips label mid-click.
	if (!isEdit.value && lookup.value.state === 'checking') return false
	if (seatBlocked.value) return false
	if (showProjectPicker.value && !allProjects.value && !selectedProjectIds.value.length) return false
	if (isModerator.value && areAllProductRowsOff(permissions.value, productRows.value)) return false
	if (scopeInvalid.value) return false
	return true
})

const { isVisible, hide } = useLegacyModalBridge(MODAL_ID, {
	onShow: () => nextTick(() => init()),
	onHide: () => resetForm(),
})

function clone(value) {
	return JSON.parse(JSON.stringify(value || {}))
}

function syncScopeModes() {
	scopeModes.value = {
		live: scopeModeFromIds(permissions.value.liveStreamAccessIds),
		vod: scopeModeFromIds(permissions.value.vodStreamAccessIds),
	}
}

function resetLookup() {
	clearTimeout(lookupTimer)
	lookupSeq += 1
	lookup.value = { state: 'idle', member: null }
	sourceGrant.value = null
	loadingSource.value = false
}

function resetForm() {
	name.value = ''
	email.value = ''
	emailError.value = ''
	emailExists.value = false
	emailLocked.value = false
	processing.value = false
	loading.value = false
	submitError.value = ''
	memberId.value = null
	permissions.value = createModeratorPreset()
	scopeModes.value = { live: 'all', vod: 'all' }
	projectScope.value = 'specific'
	selectedProjectIds.value = []
	editWorkspaceId.value = null
	resetLookup()
}

async function init() {
	processing.value = false
	submitError.value = ''
	loadStreams()
	if (isEdit.value) {
		// The workspace the Team page is standing in, which is the one whose
		// roles and permissions it just showed.
		editWorkspaceId.value = props.activeProject?.id || null
		await loadMemberPermissions()
		return
	}
	// Default invite: Moderator with conservative preset (never Admin).
	permissions.value = createModeratorPreset()
	syncScopeModes()
	name.value = ''
	email.value = ''
	emailError.value = ''
	// The workspace the Team page is standing in, as the obvious answer; the
	// picker is there to add to it, not to start from nothing.
	projectScope.value = 'specific'
	selectedProjectIds.value = props.activeProject?.id ? [props.activeProject.id] : []
	resetLookup()
	// "Add <member> to <workspace>" from a row: the person is known.
	if (props.prefill?.email) {
		name.value = props.prefill.name || ''
		email.value = props.prefill.email
		emailLocked.value = true
	}
}

async function loadStreams() {
	try {
		await store.dispatch('Streams/getStreams')
	} catch {
		// picker still opens with whatever is cached
	}
}

async function loadMemberPermissions() {
	loading.value = true
	const member = props.member
	try {
		let next
		if (member.status === 'pending') {
			const id = member.id || member._id
			memberId.value = id
			const invited = await UserService.getInvitedpermission(id)
			next = clone(invited.permissions || {})
			next.role = invited.role || next.role
		} else {
			const id = member._id
			memberId.value = id
			// Nothing to read in a workspace they are not in yet. Start from the
			// same conservative preset an invitation would rather than carrying
			// another workspace's permissions across; saving creates the grant.
			next = grantIsNew.value
				? createModeratorPreset()
				: clone(await UserService.getMemberpermission(props.teamId, id, editWorkspaceId.value))
		}
		if (!Object.prototype.hasOwnProperty.call(next, 'teamInviteUsers')) next.teamInviteUsers = false
		if (!Array.isArray(next.liveStreamAccessIds)) next.liveStreamAccessIds = []
		if (!Array.isArray(next.vodStreamAccessIds)) next.vodStreamAccessIds = []
		if (!next.role) next.role = member.role || 'moderator'
		permissions.value = next
		syncScopeModes()
	} catch (err) {
		permissions.value = clone(member.permissions || createModeratorPreset())
		syncScopeModes()
		notify({ group: 'error', text: err?.message || err || 'Could not load member permissions.' })
	} finally {
		loading.value = false
	}
}

function changeRole(nextRole) {
	if (role.value === nextRole) return
	if (nextRole === 'admin') {
		permissions.value = createAdminPermissions()
	} else {
		// Keep an already-moderator's custom perms; only reset when coming from Admin.
		permissions.value = role.value === 'admin'
			? createModeratorPreset()
			: { ...permissions.value, role: 'moderator' }
	}
	syncScopeModes()
	if (nextRole === 'moderator') loadStreams()
}

function setInviteOthers(on) {
	if (!canInviteOthers.value) return
	permissions.value = { ...permissions.value, teamInviteUsers: !!on }
}

function getLevel(row) {
	return levelFromPermissions(permissions.value, row)
}
function setLevel(row, level) {
	permissions.value = applyRowLevel(permissions.value, row, level)
	if (row.scope && level === 'none') scopeModes.value = { ...scopeModes.value, [row.scope]: 'all' }
}
function onAdvanced(row, { flag, value }) {
	permissions.value = setAdvancedFlag(permissions.value, flag, value)
}
function setChatOnly(on) {
	permissions.value = setChatModerationOnly(permissions.value, on)
}

function scopeItems(row) {
	if (row.scope === 'live') return liveStreams.value
	if (row.scope === 'vod') return vodFolders.value
	return []
}
function scopeDisabled(row) {
	if (row.scope === 'live') return !canPickStreams.value
	if (row.scope === 'vod') return !canPickVods.value
	return false
}
function scopeNoun(row) {
	return row.scope === 'vod' ? 'folders' : 'streams'
}
function setScopeMode(row, mode) {
	scopeModes.value = { ...scopeModes.value, [row.scope]: mode }
	if (mode === 'all') permissions.value = { ...permissions.value, [row.accessKey]: [] }
}
function setScopeIds(row, ids) {
	permissions.value = { ...permissions.value, [row.accessKey]: ids }
}

/**
 * The roster the page already holds answers the instant cases: an email that
 * is already on THIS workspace (member or pending) is a dead end, so it is
 * rejected before any request. Everything else goes to the server, which is
 * the source of truth for "already on the team, on which workspaces".
 */
function localMatch(value) {
	const needle = String(value || '').toLowerCase()
	return (props.members || []).find((m) => (
		String(m.email || '').toLowerCase() === needle && m.email !== props.member?.email
	)) || null
}

function onEmailInput() {
	const value = (email.value || '').trim()
	emailExists.value = false
	submitError.value = ''
	resetLookup()
	if (!value.length) {
		emailError.value = ''
		return
	}
	if (!EMAIL_RE.test(value)) {
		emailError.value = 'Please enter a valid email address.'
		return
	}
	const local = localMatch(value)
	if (local && String(local.role).toLowerCase() === 'owner') {
		emailError.value = 'This is the account owner. They already have access to every workspace.'
		emailExists.value = true
		return
	}
	// Being on this workspace already is only a dead end when this workspace is
	// the only thing on offer. With the picker there are others to add them to,
	// so defer to the lookup, which knows which ones those are.
	if (local && local.onProject !== false && !showProjectPicker.value) {
		emailError.value = local.status === 'pending'
			? `An invitation to ${projectName.value} is already pending for this email.`
			: `${local.name || 'This person'} is already on ${projectName.value}. Edit their access from the member list.`
		emailExists.value = true
		return
	}
	emailError.value = ''
	scheduleLookup(value)
}
watch(email, onEmailInput)

/** Edit a different workspace's grant: re-read, since each one is its own. */
function selectEditWorkspace(id) {
	if (!id || id === editWorkspaceId.value) return
	editWorkspaceId.value = id
	loadMemberPermissions()
}

function scheduleLookup(value) {
	if (!props.teamId) return
	lookup.value = { state: 'checking', member: null }
	const seq = lookupSeq
	lookupTimer = setTimeout(() => runLookup(value, seq), LOOKUP_DEBOUNCE_MS)
}

async function runLookup(value, seq) {
	let member = null
	try {
		const res = await UserService.lookupTeamMember(props.teamId, value)
		member = res?.member || null
	} catch (err) {
		// 404: not on the team, a plain invitation. Anything else (403 for a
		// caller who cannot invite here, a hiccup): fail open and let the
		// submit surface the real answer.
		member = null
	}
	// The email changed while this was in flight; that answer is for another question.
	if (seq !== lookupSeq) return
	if (!member) {
		lookup.value = { state: 'none', member: null }
		return
	}
	lookup.value = { state: 'found', member }
	if (member.name) name.value = member.name

	// Drop what they already hold from the selection: adding somebody should
	// never quietly rewrite the permissions they have in a workspace the form
	// was not asked about.
	const held = heldProjectIds.value
	if (held.size) selectedProjectIds.value = selectedProjectIds.value.filter((id) => !held.has(id))

	const here = (member.workspaces || []).find((w) => w.id === props.activeProject?.id)
	// "All workspaces" always has something to say, even for someone already on
	// every one of them: it is a standing grant, so it also covers the ones
	// this account has yet to create.
	const nothingLeftToAdd = allProjects.value
		? false
		: showProjectPicker.value
			? availableProjects.value.every((p) => held.has(p.id))
			: !!here && here.isEnabled !== false
	if (nothingLeftToAdd) {
		const roleLabel = here?.role ? here.role.charAt(0).toUpperCase() + here.role.slice(1) : 'a member'
		emailError.value = showProjectPicker.value
			? `${member.name || 'This person'} is already on every workspace. Edit their access from the member list.`
			: `${member.name || 'This person'} is already on ${projectName.value} as ${roleLabel}. Edit their access from the member list.`
		emailExists.value = true
	}
}

/**
 * "Use CNN permissions": the grant in another workspace, copied in as the
 * starting point for this one. Still editable before it is sent.
 */
async function usePermissionsFrom(workspace) {
	if (!existing.value || !workspace?.id || loadingSource.value) return
	loadingSource.value = true
	try {
		const source = clone(await UserService.getMemberpermission(props.teamId, existing.value.id, workspace.id))
		for (const key of ['id', '_id', '__v', 'team', 'member', 'workspace', 'workspaceId', 'isEnabled', 'createdAt', 'updatedAt']) {
			delete source[key]
		}
		if (!source.role) source.role = workspace.role || 'moderator'
		if (source.role === 'admin') {
			source.teamInviteUsers = canInviteOthers.value && source.teamInviteUsers !== false
		}
		if (!Array.isArray(source.liveStreamAccessIds)) source.liveStreamAccessIds = []
		if (!Array.isArray(source.vodStreamAccessIds)) source.vodStreamAccessIds = []
		if (!Object.prototype.hasOwnProperty.call(source, 'teamInviteUsers')) source.teamInviteUsers = false
		permissions.value = source
		syncScopeModes()
		sourceGrant.value = workspace
		if (source.role === 'moderator') loadStreams()
	} catch (err) {
		notify({ group: 'error', text: err?.message || `Could not read ${existingName.value}'s permissions in ${workspace.name}.` })
	} finally {
		loadingSource.value = false
	}
}

function resetToPreset() {
	permissions.value = createModeratorPreset()
	syncScopeModes()
	sourceGrant.value = null
}

function dismiss() {
	hide()
}

async function onConfirm() {
	if (!canSubmit.value || processing.value) return
	processing.value = true
	const payload = normalizePermissions(clone(permissions.value), {
		visibleRows: productRows.value,
		canPickStreams: canPickStreams.value,
		canPickVods: canPickVods.value,
		scopeModes: scopeModes.value,
	})
	payload.role = role.value
	// Admins still get every product flag, but `teamInviteUsers` is the one
	// Admin-only control on this form — keep the value the user just set.
	if (role.value === 'admin') {
		const inviteOthers = !!payload.teamInviteUsers
		Object.assign(payload, createAdminPermissions())
		payload.teamInviteUsers = inviteOthers
	} else {
		// The form does not offer it to a Moderator, so it does not leave one
		// set either. Demoting an Admin already lands on the preset, but an
		// existing Moderator granted it some other way would otherwise keep it
		// through every save, with nothing on screen saying so.
		payload.teamInviteUsers = false
	}

	submitError.value = ''
	try {
		if (isEdit.value) {
			const granted = grantIsNew.value
			if (props.member?.status === 'pending') {
				await UserService.updateInvitedpermission(memberId.value, payload)
			} else {
				// Named, not inferred: the grant being written is the one for the
				// workspace on screen, whatever workspace the session is in.
				await UserService.updateMemberpermission(props.teamId, memberId.value, payload, editWorkspaceId.value)
			}
			emit('update-permissions', payload)
			const where = showEditWorkspacePicker.value && editWorkspace.value ? ` in ${editWorkspace.value.name}` : ''
			notify({
				group: 'success',
				text: granted
					? `${props.member?.name} was added to ${editWorkspace.value?.name || 'the workspace'}`
					: `${props.member?.name}'s access was updated${where}`,
			})
			dismiss()
			return
		}
		// Bound explicitly to the workspaces picked, or to the one the Team page
		// is standing in when there was nothing to pick, or to the account as a
		// whole. A new person gets one invitation to accept however many
		// workspaces it covers; someone already on the team is granted them
		// right away and the response says so.
		const invite = await UserService.inviteTeamadmin(
			props.teamId,
			name.value,
			email.value.trim(),
			payload,
			allProjects.value
				? 'all'
				: (showProjectPicker.value ? [...selectedProjectIds.value] : props.activeProject?.id),
		)
		if (invite?.success) {
			const granted = invite.workspaces?.length ? invite.workspaces : [invite.workspace].filter(Boolean)
			const projectLabel = invite.allWorkspaces
				? 'every workspace'
				: granted.length > 1
					? `${granted.length} workspaces`
					: (granted[0]?.name || selectedProjectNames.value)
			if (invite.existingMember) {
				track(EVENTS.TEAM_MEMBER_INVITED, {
					invitee_role: payload.role,
					existing_member: true,
					copied_permissions: !!sourceGrant.value,
					workspace_count: granted.length,
				})
				notify({ group: 'success', text: `${invite.member?.name || existingName.value} was added to ${projectLabel}` })
				emit('member-added', invite)
			} else {
				track(EVENTS.TEAM_MEMBER_INVITED, {
					invitee_role: payload.role,
					existing_member: false,
					has_stream_scoping: !!(payload.liveStreamAccessIds?.length || payload.vodStreamAccessIds?.length),
					workspace_count: granted.length,
				})
				notify({ group: 'success', text: `You sent an invitation to ${email.value.trim()}` })
				emit('invite-sent', invite)
			}
		}
		dismiss()
	} catch (err) {
		// A 403 means the caller cannot add people to THIS workspace (an admin's
		// invite right is per project). The server's sentence says exactly that;
		// it belongs next to the form, not in a toast that disappears.
		submitError.value = err?.code === 403
			? (err?.message || `You can't add people to ${projectName.value}. Ask the account owner for invite access there.`)
			: (err?.message || (typeof err === 'string' ? err : '') || 'Something went wrong. Please try again later.')
		processing.value = false
	}
}
</script>

<template>
	<ModalBox
		v-model="isVisible"
		placement="right"
		size="md"
		scroll-body
		mobile-fullscreen
		hide-close
		footer-divider
		header-class="!hidden"
		@close="dismiss"
	>
		<div class="p-4 sm:p-6 min-w-0" data-testid="team-member-modal" data-team-member-sidebar>
			<div class="w-full min-w-0 pb-4 mb-4 border-b border-surface-3">
				<FormButton
					type="link"
					size="sm"
					class="lg:hidden !text-surface-8 hover:!text-white !px-0 mb-3"
					@click="dismiss"
				>
					<IconBase fill="none" class="w-4 h-4">
						<IconChevronLeft />
					</IconBase>
					Back
				</FormButton>
				<h4 class="text-lg mb-1 font-medium break-words">{{ title }}</h4>
				<p class="text-sm text-surface-7 font-normal mb-0">{{ subtitle }}</p>
			</div>

			<Spinner
				v-if="loading"
				text="Retrieving data..."
				classes="text-dark-8 my-6"
				spinner-color="var(--c-dark-8)"
				spinner-size="15px"
			/>

			<template v-else>
				<!-- Who -->
				<div v-if="!isEdit" class="flex flex-col gap-4 mb-5">
					<FormInput
						label="Name"
						v-model="name"
						type="text"
						size="md"
						:disabled="!!existing || emailLocked"
						@keydown.enter="onConfirm"
					/>
					<div>
						<FormInput
							label="Email address"
							v-model="email"
							type="email"
							size="md"
							:is-error="!!emailError"
							:disabled="emailLocked"
							@keydown.enter="onConfirm"
						/>
						<div v-if="emailError" class="color-danger text-s-s font-normal mt-2 flex items-start justify-start">
							<IconBase v-if="!emailExists" class="mr-1 mt-1" width="16" height="15" width-view-box="16" height-view-box="15">
								<IconMediaFileWarning />
							</IconBase>
							<div :class="{ 'text-surface-6': emailExists }">{{ emailError }}</div>
						</div>
						<p v-else-if="lookup.state === 'checking'" class="mt-2 mb-0 flex items-center gap-2 text-tiny text-surface-6" aria-live="polite">
							<Spinner spinner-color="var(--c-surface-6)" spinner-size="12px" />
							Checking whether they're already on your team
						</p>
					</div>

					<!-- Already on the team, not on this workspace: a grant, not an invitation. -->
					<div
						v-if="isGrant"
						class="rounded-lg border border-surface-4 bg-surface-3 p-3"
						data-testid="invite-existing-prompt"
						aria-live="polite"
					>
						<p class="mb-0 text-sm text-surface-text-2">
							<span class="font-medium text-white">{{ existingName }}</span>
							<template v-if="pausedHere"> is on {{ projectName }}, but paused.</template>
							<template v-else-if="otherGrantsSentence"> is already on your team: {{ otherGrantsSentence }}.</template>
							<template v-else> is already on your team.</template>
							<template v-if="!pausedHere"> Add them to {{ projectName }}?</template>
						</p>
						<template v-if="otherGrants.length">
							<p class="mt-2 mb-1.5 text-tiny text-surface-7">
								Set their access below, or start from what they already have:
							</p>
							<div class="flex flex-wrap items-center gap-2">
								<FormButton
									v-for="workspace in otherGrants"
									:key="workspace.id"
									type="secondary"
									size="sm"
									:disabled="loadingSource || processing"
									:class="sourceGrant?.id === workspace.id ? '!ring-1 !ring-main' : ''"
									:aria-label="`Use ${workspace.name} permissions`"
									@click="usePermissionsFrom(workspace)"
								>
									<ProjectDot :color="workspace.color" size="size-1.5" class="mr-1.5" />
									Use {{ workspace.name }} permissions
								</FormButton>
								<FormButton
									v-if="sourceGrant"
									type="link"
									size="sm"
									class="!text-surface-8 hover:!text-white"
									:disabled="loadingSource || processing"
									@click="resetToPreset"
								>
									Reset
								</FormButton>
							</div>
							<p v-if="sourceGrant" class="mt-2 mb-0 text-tiny text-surface-6">
								Starting from their {{ sourceGrant.name }} permissions. Adjust anything below before adding.
							</p>
						</template>
					</div>

					<!-- A new person at the seat limit: the server would refuse. -->
					<Alert v-if="seatBlocked" type="warning2" class="!items-start">
						<div>
							<p class="mb-0.5 text-sm font-medium">All {{ seatLimit }} seats on your plan are in use</p>
							<p class="mb-0 text-tiny">
								Inviting someone new needs a free seat.
								<template v-if="scopedToProject">You can still add someone who's already on your team to {{ projectName }}: they keep their one seat.</template>
							</p>
						</div>
					</Alert>
				</div>

				<!-- Which workspace this edit is about. A member can hold a
				     different role in each, so the form has to say which one it
				     is showing — and picking one they are not in yet is how they
				     get added to it, without a second invitation. -->
				<div v-if="showEditWorkspacePicker" class="mb-5" data-testid="edit-workspace-picker">
					<label class="block font-medium text-tiny text-surface-text-1 mb-2">Workspace</label>
					<div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
						<button
							v-for="project in availableProjects"
							:key="project.id"
							type="button"
							class="flex items-center gap-2 text-left rounded-lg border p-2.5 transition-colors"
							:class="project.id === editWorkspaceId ? 'border-main bg-surface-3' : 'border-surface-4 hover:border-surface-6'"
							@click="selectEditWorkspace(project.id)"
						>
							<ProjectDot :color="project.color" />
							<span class="min-w-0 flex-1 truncate text-sm font-medium text-surface-text-2">{{ project.name }}</span>
							<span v-if="!memberWorkspaceIds.has(project.id)" class="shrink-0 text-tiny text-surface-6">Not added</span>
							<IconBase v-else-if="project.id === editWorkspaceId" class="w-4 h-4 shrink-0 text-main"><IconCheckmark /></IconBase>
						</button>
					</div>
					<p v-if="grantIsNew" class="mb-0 mt-2 text-tiny text-surface-7">
						{{ member?.name || 'This person' }} isn't in {{ editWorkspace?.name }} yet. Saving adds them with the access you set here.
					</p>
				</div>

				<!-- Role choice-cards -->
				<div class="mb-5">
					<label class="block font-medium text-tiny text-surface-text-1 mb-2">Role</label>
					<div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
						<button
							type="button"
							class="text-left rounded-lg border p-3 transition-colors"
							:class="!isModerator ? 'border-main bg-surface-3' : 'border-surface-4 hover:border-surface-6'"
							@click="changeRole('admin')"
						>
							<span class="flex items-center gap-2 text-sm font-medium text-surface-text-2">
								Admin
								<IconBase v-if="!isModerator" class="w-4 h-4 text-main"><IconCheckmark /></IconBase>
							</span>
							<span class="block mt-0.5 text-tiny text-surface-7">Full access to everything, including billing and team settings.</span>
						</button>
						<button
							type="button"
							class="text-left rounded-lg border p-3 transition-colors"
							:class="isModerator ? 'border-main bg-surface-3' : 'border-surface-4 hover:border-surface-6'"
							@click="changeRole('moderator')"
						>
							<span class="flex items-center gap-2 text-sm font-medium text-surface-text-2">
								Moderator
								<IconBase v-if="isModerator" class="w-4 h-4 text-main"><IconCheckmark /></IconBase>
							</span>
							<span class="block mt-0.5 text-tiny text-surface-7">Choose exactly what they can see and do.</span>
						</button>
					</div>
				</div>

				<!-- Admin only. The server would honour this flag for a Moderator
				     too, but inviting teammates is not something the Moderator
				     role is meant to offer — so it is not asked there, and
				     `onConfirm` clears it on save rather than letting a stale
				     true survive out of sight of the form.
				     Shown without the plan too, dimmed and disabled, so the
				     tooltip can say why rather than leaving a setting people
				     were told about simply missing. -->
				<template v-if="!isModerator">
					<div
						class="flex items-center justify-between gap-3 rounded-lg border border-surface-3 p-3 mb-3"
						:class="{ 'opacity-65': !canInviteOthers }"
					>
						<div class="min-w-0">
							<p class="mb-0 text-sm font-medium text-surface-text-2">Can invite other members</p>
							<p class="mb-0 mt-0.5 text-tiny text-surface-7">Let this Admin add and manage teammates.</p>
						</div>
						<Tooltip
							wrapper-class="inline-flex shrink-0"
							wrap-trigger
							:content="inviteTooltip"
							:active="!canInviteOthers"
						>
							<FormSwitch
								:model-value="!!permissions.teamInviteUsers"
								:disabled="!canInviteOthers"
								@update:model-value="setInviteOthers"
							/>
						</Tooltip>
					</div>
					<Alert type="normal" class="mb-2">
						<div>
							<p class="mb-1 text-sm font-medium text-surface-9">Admins get everything</p>
							<p class="mb-0 text-tiny text-surface-7">
								Every feature, all content, and billing. To limit access, assign the Moderator role instead.
							</p>
						</div>
					</Alert>
				</template>

				<!-- Moderator: per-area levels -->
				<div v-else>
					<div class="mb-1">
						<h5 class="mb-0.5 text-base font-medium text-white">Permissions</h5>
						<p class="mb-0 text-tiny text-surface-7">Edit includes viewing and start/stop. Full adds delete.</p>
					</div>
					<div v-for="section in productSections" :key="section.id" class="mt-4 first:mt-3">
						<p class="mb-1 text-xs font-semibold tracking-wider text-surface-6 select-none uppercase">{{ section.title }}</p>
						<div class="rounded-lg border border-surface-3 px-3">
							<TeamPermissionRow
								v-for="row in section.rows"
								:key="row.id"
								:label="row.label"
								:description="row.description"
								:kind="row.kind"
								:levels="row.levels || ['none','view','edit','full']"
								:level="getLevel(row)"
								:advanced="(row.advanced || []).map((a) => ({ ...a, value: !!permissions[a.flag] }))"
								:has-scope="!!row.scope"
								@update:level="setLevel(row, $event)"
								@toggle-advanced="onAdvanced(row, $event)"
							>
								<template v-if="chatOnlyApplies(row, getLevel(row))" #modifier>
									<div class="flex items-center justify-between gap-3">
										<div class="min-w-0">
											<p class="mb-0 text-sm font-medium text-surface-text-2">Moderate Chat only</p>
											<p class="mb-0 mt-0.5 text-tiny text-surface-7">Moderate chat of pre-defined livestream</p>
										</div>
										<FormSwitch
											:model-value="!!permissions.liveStreamChatModerationOnly"
											@update:model-value="setChatOnly"
										/>
									</div>
								</template>
								<template #scope>
									<TeamContentScopePicker
										:name="`scope-${row.id}`"
										:items="scopeItems(row)"
										:model-value="permissions[row.accessKey] || []"
										:mode="scopeModes[row.scope]"
										:noun="scopeNoun(row)"
										:scope-locked="scopeDisabled(row)"
										:lock-message="scopeLockMessage(row.scope)"
										@update:mode="setScopeMode(row, $event)"
										@update:model-value="setScopeIds(row, $event)"
									/>
								</template>
							</TeamPermissionRow>
						</div>
					</div>
				</div>

				<!-- Which workspaces this covers. One decision, one email; the
				     permissions above apply to each of them. -->
				<div v-if="showProjectPicker" class="mt-5" data-testid="invite-project-picker">
					<div class="mb-2">
						<h5 class="mb-0.5 text-base font-medium text-white">Workspaces</h5>
						<p class="mb-0 text-tiny text-surface-7">
							Where this person can work. You can change it later, per workspace.
						</p>
					</div>
					<TeamContentScopePicker
						name="invite-workspaces"
						noun="workspaces"
						all-label="All workspaces"
						all-hint="Access all current and future workspaces."
						specific-label="Selected workspaces"
						specific-hint="Only access specific workspaces."
						:items="projectItems"
						:model-value="selectedProjectIds"
						:mode="projectScope"
						:disabled="processing"
						:locked-ids="lockedProjectIds"
						locked-note="Already has access"
						@update:mode="projectScope = $event"
						@update:model-value="selectedProjectIds = $event"
					/>
				</div>
			</template>
		</div>

		<template #footer>
			<div class="px-4 sm:px-6 py-4 w-full flex flex-col gap-3">
				<Alert v-if="submitError" type="error" :text="submitError" data-testid="invite-submit-error" />
				<div class="flex flex-col sm:flex-row sm:items-center gap-3">
					<p class="mb-0 min-w-0 flex-1 text-sm text-surface-7">{{ summarySentence }}</p>
					<div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 shrink-0">
						<FormButton type="secondary" size="md" class="w-full sm:w-auto justify-center" @click="dismiss">
							Cancel
						</FormButton>
						<FormButton
							type="main"
							size="md"
							class="w-full sm:w-auto justify-center"
							:disabled="!canSubmit"
							:is-loading="processing"
							@click="onConfirm"
						>
							{{ primaryLabel }}
						</FormButton>
					</div>
				</div>
			</div>
		</template>
	</ModalBox>
</template>

<style>
/* Keep support reachable without covering this sidebar's sticky actions.
   The sidebar is teleported to <body> and only rendered while open, so its
   marker drives this. Intercom regenerates its hashed classes, so match its
   stable iframe names and the wrappers that hold them instead. */
@media (min-width: 1024px) {
	body:has([data-team-member-sidebar]) :is(
		.intercom-lightweight-app-launcher,
		.intercom-lightweight-app-messenger,
		iframe[name="intercom-launcher-frame"],
		/* iframe[name="intercom-messenger-frame"], */
		div:has(> iframe[name="intercom-launcher-frame"]),
		div:has(> iframe[name="intercom-messenger-frame"])
	) {
		right: 534px !important;
	}
}
</style>
